import React, { useEffect, useMemo, useState } from 'react';
import { ShieldAlert, Loader2, Lock } from 'lucide-react';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import { fetchFeatureAccess } from '../../features/journal/api/journal';
import { calcSustain } from '../../features/tools/calc';

const inputClass = 'w-full bg-[#050914] border border-amber-400/30 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-amber-400 focus:ring-1 focus:ring-amber-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';
const num = (s: string) => (s.trim() === '' ? 0 : Number(s));

function Row({ label, value, strong, accent }: { label: string; value: React.ReactNode; strong?: boolean; accent?: boolean }) {
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 ${strong ? 'bg-cyan-400/10' : ''}`}>
      <span className="text-sm text-white/60">{label}</span>
      <span className={`text-sm font-medium ${accent ? 'text-cyan-400' : strong ? 'text-white text-base' : 'text-white/90'}`}>{value}</span>
    </div>
  );
}

export default function SustainingPowerPage() {
  const [access, setAccess] = useState<boolean | null>(null);
  const [equity, setEquity] = useState('100');
  const [lotOpen, setLotOpen] = useState('0.05');
  const [side, setSide] = useState<'BUY' | 'SELL'>('SELL');
  const [priceNow, setPriceNow] = useState('2980');
  const [defensePrice, setDefensePrice] = useState('3020');

  useEffect(() => {
    fetchFeatureAccess().then((a) => setAccess(a.harga_pertahanan)).catch(() => setAccess(false));
  }, []);

  const result = useMemo(() => calcSustain({
    equity: num(equity), lotOpen: num(lotOpen), side, priceNow: num(priceNow), defensePrice: num(defensePrice),
    contractSize: 100, pipsPerPoint: 10, lotMin: 0.01,
  }), [equity, lotOpen, side, priceNow, defensePrice]);

  if (access === null) return <DashboardLayout navItems={MEMBER_NAV} title="Harga Pertahanan"><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div></DashboardLayout>;
  if (!access) return (
    <DashboardLayout navItems={MEMBER_NAV} title="Harga Pertahanan">
      <div data-testid="sustain-no-access" className="max-w-md mx-auto text-center py-20">
        <Lock className="w-10 h-10 text-white/30 mx-auto mb-4" />
        <p className="text-white/60">Anda belum memiliki akses ke kalkulator Harga Pertahanan. Hubungi admin.</p>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="Harga Pertahanan">
      <div className="max-w-4xl" data-testid="sustain-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center"><ShieldAlert className="w-5 h-5 text-amber-400" strokeWidth={1.5} /></div>
          <div><p className="text-xs uppercase tracking-[0.2em] text-amber-400">Sustaining Power</p><h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Harga Pertahanan</h2></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-4">
            <p className="text-xs uppercase tracking-wider text-amber-400/80">Input (isi kolom kuning)</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelClass}>Equity akun ($)</label><input data-testid="sustain-equity" type="number" step="any" className={inputClass} value={equity} onChange={(e) => setEquity(e.target.value)} /></div>
              <div><label className={labelClass}>Lot terbuka</label><input data-testid="sustain-lot" type="number" step="any" className={inputClass} value={lotOpen} onChange={(e) => setLotOpen(e.target.value)} /></div>
              <div><label className={labelClass}>Arah posisi terbuka</label><select className={inputClass} value={side} onChange={(e) => setSide(e.target.value as any)}><option value="BUY" className="bg-[#090D1A]">BUY</option><option value="SELL" className="bg-[#090D1A]">SELL</option></select></div>
              <div><label className={labelClass}>Harga sekarang</label><input type="number" step="any" className={inputClass} value={priceNow} onChange={(e) => setPriceNow(e.target.value)} /></div>
              <div className="col-span-2"><label className={labelClass}>Harga pertahanan</label><input data-testid="sustain-defense" type="number" step="any" className={inputClass} value={defensePrice} onChange={(e) => setDefensePrice(e.target.value)} /></div>
            </div>
            <p className="text-xs text-white/40">Dihitung untuk XAU/USD (1 lot = 100 oz, 1 point = 10 pips).</p>
          </div>

          <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden divide-y divide-white/5" data-testid="sustain-result">
            <p className="text-xs uppercase tracking-wider text-cyan-400/80 px-4 py-3">Hasil perhitungan</p>
            <Row label="Ketahanan saat ini (point)" value={result.sustainNowPoint} />
            <Row label="Ketahanan saat ini (pips)" value={result.sustainNowPips} />
            <Row label="Jarak ke harga pertahanan (point)" value={result.distancePoint} />
            <Row label="Equity ÷ jarak" value={result.equityDivDist} />
            <Row label="Lot maksimal (dibulatkan bawah)" value={<span data-testid="sustain-lotmax">{result.lotMax}</span>} strong accent />
            <Row label="Lot hedging yang dibutuhkan" value={<span data-testid="sustain-hedge">{result.lotHedge}</span>} strong />
            <Row label="Arah hedging" value={<span className={result.lotHedge === 0 ? 'text-emerald-400' : 'text-amber-400'}>{result.hedgeDir}</span>} />
          </div>
        </div>
        <p className="text-xs text-white/40 mt-4">Hedging Sustaining Power diperbolehkan untuk menjaga akun tetap hidup sampai harga pertahanan, dan TIDAK membatalkan garansi.</p>
      </div>
    </DashboardLayout>
  );
}
